import { Container, Graphics } from 'pixi.js';

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: number;
  alpha: number;
  rotation: number;
  rotationSpeed: number;
  gravity: number;
}

export class ParticleSystem {
  private container: Container;
  private particles: Particle[] = [];
  private graphics: Graphics;
  
  constructor(container: Container) {
    this.container = container;
    this.graphics = new Graphics();
    this.container.addChild(this.graphics);
  }
  
  public emit(
    x: number,
    y: number,
    count: number,
    options: {
      colors?: number[];
      minSize?: number;
      maxSize?: number;
      minSpeed?: number;
      maxSpeed?: number;
      gravity?: number;
      lifetime?: number;
      spread?: number;
    } = {}
  ): void {
    const {
      colors = [0xFFD700, 0xFF6B35, 0x3DBF47, 0xFFD23F, 0xFF69B4],
      minSize = 3,
      maxSize = 8,
      minSpeed = 2,
      maxSpeed = 6,
      gravity = 0.1,
      lifetime = 60,
      spread = Math.PI * 2
    } = options;
    
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * spread - spread / 2 - Math.PI / 2;
      const speed = minSpeed + Math.random() * (maxSpeed - minSpeed);
      const color = colors[Math.floor(Math.random() * colors.length)];
      
      const particle: Particle = {
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: lifetime + Math.random() * 30,
        maxLife: lifetime + Math.random() * 30,
        size: minSize + Math.random() * (maxSize - minSize),
        color,
        alpha: 1,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        gravity
      };
      
      this.particles.push(particle);
    }
  }
  
  public update(delta: number): void {
    this.graphics.clear();
    
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      
      p.vy += p.gravity;
      p.x += p.vx * delta;
      p.y += p.vy * delta;
      p.rotation += p.rotationSpeed;
      p.life -= delta;
      p.alpha = Math.max(0, p.life / p.maxLife);
      
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      
      this.graphics.circle(p.x, p.y, p.size * p.alpha);
      this.graphics.fill({ color: p.color, alpha: p.alpha });
    }
  }
  
  public clear(): void {
    this.particles = [];
    this.graphics.clear();
  }
  
  public burst(x: number, y: number, intensity: 'small' | 'medium' | 'big'): void {
    switch (intensity) {
      case 'small':
        this.emit(x, y, 20, {
          colors: [0xFFD700, 0xFFFFFF],
          minSize: 2,
          maxSize: 5,
          lifetime: 40
        });
        break;
        
      case 'medium':
        this.emit(x, y, 50, {
          colors: [0xFFD700, 0xFF6B35, 0x3DBF47],
          minSize: 3,
          maxSize: 7,
          lifetime: 50
        });
        break;
        
      case 'big':
        this.emit(x, y, 100, {
          colors: [0xFFD700, 0xFF6B35, 0x3DBF47, 0xFFD23F, 0xFF69B4],
          minSize: 4,
          maxSize: 10,
          maxSpeed: 8,
          lifetime: 70
        });
        break;
    }
  }
}